import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { FileUp, Upload, Loader2, FileText, Trash2, Sparkles, Eye } from 'lucide-react';

const LOCATIONS = ['Carrollwood', 'Countryside', 'Lakeland', 'Sarasota', 'Wesley Chapel'];
const FORMATS = [
  { key: 'scenario', label: 'Scenario-based', hint: 'Real situation → what would you do' },
  { key: 'short_answer', label: 'Short answer', hint: 'Explain in own words, AI-graded' },
  { key: 'ordering', label: 'Ordering', hint: 'Drag steps into order' },
  { key: 'multi_select', label: 'Select all that apply', hint: '' },
  { key: 'multiple_choice', label: 'Multiple choice', hint: 'Optional — simple recall only' },
];

export default function UploadTab({ documents, uploading, handleFileUpload, onDeleteDocument, isAdmin }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState('general');
  const [position, setPosition] = useState('');
  const [targetAudience, setTargetAudience] = useState('both');
  const [location, setLocation] = useState('');
  const [moduleType, setModuleType] = useState('smart');
  const [pathId, setPathId] = useState('');
  const [formats, setFormats] = useState(['scenario', 'short_answer', 'ordering', 'multi_select']);
  const toggleFormat = (k) => setFormats(prev => prev.includes(k) ? prev.filter(f => f !== k) : [...prev, k]);
  const [positions, setPositions] = useState([]);
  const [paths, setPaths] = useState([]);

  useEffect(() => {
    api('get', '/learning-paths').then(res => setPaths(res.data)).catch(() => setPaths([]));
  }, [api, documents]);

  useEffect(() => {
    if (category === 'FOH' || category === 'BOH') {
      api('get', `/positions?department=${category}`)
        .then(res => setPositions(res.data))
        .catch(() => setPositions([]));
    } else {
      setPositions([]);
      setPosition('');
    }
  }, [category, api]);

  const onFileChange = (e) => {
    handleFileUpload(e, { category, position, targetAudience, location, moduleType, pathId, questionFormats: formats.join(',') });
  };

  const isDeptSelected = category === 'FOH' || category === 'BOH';
  const canUpload = formats.length > 0;

  return (
    <>
      <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="upload-section">
        <CardContent className="p-6">
          <div className="border-2 border-dashed border-stone-300 rounded-2xl p-8 text-center hover:border-[#1B2A4A] transition-colors">
            <Upload className="w-12 h-12 text-stone-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-stone-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Upload Restaurant Document</h3>
            <p className="text-sm text-stone-500 mb-4">Upload a PDF or Word document and AI will create training modules, tests, and tasks automatically</p>
            <div className="max-w-md mx-auto space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500 font-medium">Department</Label>
                  <Select value={category} onValueChange={(v) => { setCategory(v); setPosition(''); }}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General (All)</SelectItem>
                      <SelectItem value="FOH">FOH</SelectItem>
                      <SelectItem value="BOH">BOH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-stone-500 font-medium">Target Audience</Label>
                  <Select value={targetAudience} onValueChange={setTargetAudience}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="upload-audience-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Staff & Managers</SelectItem>
                      <SelectItem value="staff">Staff Only</SelectItem>
                      <SelectItem value="manager">Managers Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500 font-medium">Module Type</Label>
                  <Select value={moduleType} onValueChange={setModuleType}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="upload-module-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smart">Smart Parse — one module per section</SelectItem>
                      <SelectItem value="single">Single Module (short docs)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-stone-500 font-medium">Location</Label>
                  <Select value={location || 'all'} onValueChange={(v) => setLocation(v === 'all' ? '' : v)}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="upload-location-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {LOCATIONS.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {moduleType === 'smart' ? (
                <p className="text-xs text-[#1B2A4A] bg-[#1B2A4A]/5 rounded-lg px-3 py-2 flex items-start gap-2 text-left" data-testid="smart-parse-hint"><Sparkles className="w-4 h-4 text-[#D9480F] shrink-0 mt-0.5" /><span>AI analyzes the document's structure and creates <strong>one module (lessons + quiz + practical checklist) per logical section</strong> — no limit. Documents over 15 pages (or with Day/Shift structure) also get a day-by-day schedule. Everything lands as a <strong>draft pathway</strong> you review before publishing.</span></p>
              ) : (
                <div>
                <Label className="text-xs text-stone-500 font-medium">Add to Learning Path</Label>
                <Select value={pathId || 'none'} onValueChange={(v) => setPathId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="upload-path-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't add to a path (standalone)</SelectItem>
                    {paths.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.position ? ` (${p.position})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                {pathId && <p className="text-[11px] text-stone-500 mt-1">The generated module will be appended to the end of this path.</p>}
                </div>
              )}
              <div className="text-left">
                <Label className="text-xs text-stone-500 font-medium">Quiz Question Formats</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1" data-testid="question-formats">
                  {FORMATS.map(f => (
                    <label key={f.key} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-left ${formats.includes(f.key) ? 'border-[#1B2A4A] bg-[#1B2A4A]/5' : 'border-stone-200'}`}>
                      <input type="checkbox" checked={formats.includes(f.key)} onChange={() => toggleFormat(f.key)} className="mt-0.5 accent-[#1B2A4A]" data-testid={`format-${f.key}`} />
                      <span className="text-xs"><span className="font-medium text-stone-800">{f.label}</span>{f.hint && <span className="text-stone-400 block">{f.hint}</span>}</span>
                    </label>
                  ))}
                </div>
                {formats.length === 0 && <p className="text-xs text-amber-600 mt-1">Select at least one format</p>}
              </div>
              {isDeptSelected && (
                <div>
                  <Label className="text-xs text-stone-500 font-medium">Position <span className="text-stone-400">(optional)</span></Label>
                  <Select value={position || 'none'} onValueChange={(v) => setPosition(v === 'none' ? '' : v)}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="upload-position-select"><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any position</SelectItem>
                      <SelectItem value="All Positions">All Positions</SelectItem>
                      {positions.map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <label className="inline-block cursor-pointer">
              <input type="file" accept=".pdf,.docx,.doc" onChange={onFileChange} className="hidden" disabled={uploading || !canUpload} data-testid="file-upload-input" />
              <Button asChild disabled={uploading || !canUpload} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="upload-button">
                <span>{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing with AI...</> : <><FileUp className="w-4 h-4 mr-2" /> Choose Document</>}</span>
              </Button>
            </label>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Uploaded Documents</CardTitle></CardHeader>
        <CardContent>
          {documents.length === 0 ? <p className="text-sm text-stone-500 text-center py-4">No documents uploaded yet</p> : (
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-100" data-testid={`doc-${doc.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FileText className="w-5 h-5 text-[#D9480F] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{doc.original_filename}</p>
                      <p className="text-xs text-stone-500">
                        By {doc.uploaded_by_name} - {new Date(doc.created_at).toLocaleDateString()}
                        {doc.position && <span className="ml-1">| {doc.position}</span>}
                        {doc.path_name && <span className="ml-1">| Path: {doc.path_name}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0 ml-2">
                    <Badge className={
                      doc.category === 'FOH' ? 'bg-blue-100 text-blue-700' :
                      doc.category === 'BOH' ? 'bg-orange-100 text-orange-700' :
                      'bg-stone-100 text-stone-600'
                    }>
                      {doc.category === 'FOH' ? 'FOH' : doc.category === 'BOH' ? 'BOH' : 'All'}
                    </Badge>
                    {doc.target_audience && doc.target_audience !== 'both' && (
                      <Badge className={doc.target_audience === 'manager' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}>
                        {doc.target_audience === 'manager' ? 'Mgr' : 'Staff'}
                      </Badge>
                    )}
                    {doc.location && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{doc.location}</Badge>
                    )}
                    {doc.position && (
                      <Badge className="bg-violet-100 text-violet-700 text-[10px]">{doc.position}</Badge>
                    )}
                    {doc.path_name && (
                      <Badge className="bg-teal-100 text-teal-700 text-[10px]">{doc.path_name}</Badge>
                    )}
                    {doc.module_type === 'smart' && doc.module_count > 0 && <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A] text-[10px]">{doc.module_count} modules{doc.schedule?.length ? ` · ${doc.schedule.length}-day schedule` : ''}</Badge>}
                    <Badge className={doc.status === 'completed' ? 'bg-green-100 text-green-700' : doc.status === 'review' ? 'bg-amber-100 text-amber-700' : doc.status === 'processing' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'} data-testid={`doc-status-${doc.id}`}>{doc.status === 'review' ? 'needs review' : doc.status}</Badge>
                    {doc.module_type === 'smart' && doc.status !== 'failed' && (
                      <Button size="sm" onClick={() => navigate(`/admin/pathway/${doc.id}`)} className={`rounded-lg h-7 text-xs ${doc.status === 'review' ? 'bg-[#D9480F] hover:bg-[#c2410c] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid={`review-pathway-${doc.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> {doc.status === 'review' ? 'Review Pathway' : 'View'}
                      </Button>
                    )}
                    {isAdmin && onDeleteDocument && (
                      <Button variant="ghost" size="sm" onClick={() => onDeleteDocument(doc.id)}
                        className="text-red-400 hover:text-red-600 p-1 h-7 w-7" data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
